import {
    Box,
    Device,
    DeviceListener,
    LocationCodeListener,
    LockStatus,
    LockStatusListener,
    Occupancy,
    SetupUrlListener
} from "@variocube/app-api";
import {
    BoxListener,
    ConnectionListener,
    InstalledAppsListener,
    InternetConnection,
    InternetConnectionListener,
    OccupancyListener,
    StorageListener,
    SupportInfo
} from "./Controller";
import {EventSource} from "../EventSource";

export class AbstractController {

    private readonly lockStatusListeners = new Set<LockStatusListener>();
    private readonly connectionListeners = new Set<ConnectionListener>();
    private readonly locationCodeListener = new Set<LocationCodeListener>();
    private readonly installedAppsListener = new Set<InstalledAppsListener>();
    private readonly occupancyListener = new Set<OccupancyListener>();
    private readonly boxListener = new Set<BoxListener>();
    private readonly deviceListener = new Set<DeviceListener>();
    private readonly internetConnectionListener = new Set<InternetConnectionListener>();
    public readonly supportInfo = new EventSource<SupportInfo>();
    private readonly storageListener = new Set<StorageListener>();
    private readonly setupUrlListener = new Set<SetupUrlListener>();

    private _locationCode = "";
    private _setupUrl: string|null = "";

    constructor() {
    }

    get locationCode() {
        return this._locationCode;
    }
    protected setLocationCode(code: string) {
        this._locationCode = code;
        this.onLocationCode(code);
    }

    protected setSetupUrl(setupUrl: string|null) {
        this._setupUrl = setupUrl;
        this.onSetupUrl(setupUrl);
    }

    addLockStatusListener(listener: LockStatusListener) {
        this.lockStatusListeners.add(listener);
    }

    removeLockStatusListener(listener: LockStatusListener) {
        this.lockStatusListeners.delete(listener);
    }

    addConnectionListener(listener: ConnectionListener) {
        this.connectionListeners.add(listener);
    }

    removeConnectionListener(listener: ConnectionListener) {
        this.connectionListeners.delete(listener);
    }

    addLocationCodeListener(listener: LocationCodeListener) {
        this.locationCodeListener.add(listener);
    }

    removeLocationCodeListener(listener: LocationCodeListener) {
        this.locationCodeListener.delete(listener);
    }

    addSetupUrlListener(listener: SetupUrlListener) {
        this.setupUrlListener.add(listener);
    }

    removeSetupUrlListener(listener: SetupUrlListener) {
        this.setupUrlListener.delete(listener);
    }

    addStorageListener(listener: StorageListener) {
        this.storageListener.add(listener);
    }

    removeStorageListener(listener: StorageListener) {
        this.storageListener.delete(listener);
    }

    addInstalledAppsListener(listener: InstalledAppsListener) {
        this.installedAppsListener.add(listener);
    }

    removeInstalledAppsListener(listener: InstalledAppsListener) {
        this.installedAppsListener.delete(listener);
    }

    addInternetConnectionListener(listener: InternetConnectionListener) {
        this.internetConnectionListener.add(listener);
    }

    removeInternetConnectionListener(listener: InternetConnectionListener) {
        this.internetConnectionListener.delete(listener);
    }

    addOccupancyListener(listener: OccupancyListener) {
        this.occupancyListener.add(listener);
    }

    removeOccupancyListener(listener: OccupancyListener) {
        this.occupancyListener.delete(listener);
    }

    addBoxListener(listener: BoxListener) {
        this.boxListener.add(listener);
    }

    removeBoxListener(listener: BoxListener) {
        this.boxListener.delete(listener);
    }

    addDeviceListener(listener: DeviceListener) {
        this.deviceListener.add(listener);
    }

    removeDeviceListener(listener: DeviceListener) {
        this.deviceListener.delete(listener);
    }

    protected onLockStatusChanged(lock: string, status: LockStatus) {
        this.lockStatusListeners.forEach(listener => {
            try {
                listener(lock, status)
            }
            catch (error) {
                console.error("Unhandled error from lock status listener", error);
            }
        });
    }

    protected onConnected(connected: boolean) {
        this.connectionListeners.forEach(listener => {
            try {
                listener(connected);
            }
            catch (error) {
                console.error("Unhandled error from connection listener", error);
            }
        });
    }

    private onLocationCode(code: string) {
        this.locationCodeListener.forEach(listener => {
            try {
                listener(code)
            }
            catch (error) {
                console.error("Unhandled error from location code listener", error);
            }
        });
    }

    private onSetupUrl(setupUrl: string|null) {
        this.setupUrlListener.forEach(listener => {
            try {
                listener(setupUrl);
            } catch (err) {
                console.error('Unhandled error from setup key listener', err);
            }
        });
    }

    protected onInstalledAppsChanged(apps: string[]) {
        this.installedAppsListener.forEach(listener => {
            try {
                listener(apps);
            }
            catch (error) {
                console.error("Unhandled error from installed apps listener", error);
            }
        });
    }

    protected onInternetConnectionChanged(connection: InternetConnection) {
        this.internetConnectionListener.forEach(listener => {
            try {
                listener(connection);
            }
            catch (error) {
                console.error("Unhandled error from internet connection listener", error);
            }
        });
    }

    protected onStorageItemChanged(appId: string, key: string) {
        this.storageListener.forEach(listener => {
            try {
                listener(appId, key);
            }
            catch (error) {
                console.error("Unhandled error from storage listener", error);
            }
        });
    }

    protected onOccupancyCreated(occupancy: Occupancy) {
        this.occupancyListener.forEach(listener => {
            try {
                listener.created(occupancy);
            }
            catch (error) {
                console.error("Unhandled error from occupancy listener", error);
            }
        })
    }

    protected onOccupancyUpdated(occupancy: Occupancy) {
        this.occupancyListener.forEach(listener => {
            try {
                listener.updated(occupancy);
            }
            catch (error) {
                console.error("Unhandled error from occupancy listener", error);
            }
        })
    }

    protected onOccupancyAccessChanged(occupancy: Occupancy) {
        this.occupancyListener.forEach(listener => {
            try {
                listener.accessChanged(occupancy);
            }
            catch (error) {
                console.error("Unhandled error from occupancy listener", error);
            }
        })
    }

    protected onOccupancyEnded(uuid: string) {
        this.occupancyListener.forEach(listener => {
            try {
                listener.ended(uuid);
            }
            catch (error) {
                console.error("Unhandled error from occupancy listener", error);
            }
        })
    }

    protected onBoxesChanged(boxes: Box[]) {
        this.boxListener.forEach(listener => {
            try {
                listener(boxes);
            }
            catch (error) {
                console.error("Unhandled error from box listener", error);
            }
        })
    }

    protected onDevicesChanged(devices: Device[]) {
        this.deviceListener.forEach(listener => {
            try {
                listener(devices);
            }
            catch (error) {
                console.error("Unhandled error from device listener", error);
            }
        })
    }
}